/**
 * The guided tour (spec §2.6): a spotlight on the real control + a tooltip
 * card, across screens, skippable at every step, resumable across a reload,
 * restartable from the sidebar.
 *
 * How it never blocks the product: the dim layer is ONE fixed element with
 * `pointer-events: none`; the spotlight is a transparent box whose huge
 * `box-shadow` paints the dim around it. Only the card takes pointer events,
 * so the anchored control — and everything else on the page — stays
 * clickable. The user may do the real action; the tour advances on Next.
 * (The four-rect variant that blocks the rest of the page was not worth its
 * complexity: "the tour never blocks the product" is the invariant, §3.)
 *
 * The anchor is `[data-tour="<step.anchor>"]`, measured on step change,
 * resize, scroll (capture), a body ResizeObserver and a body
 * MutationObserver (screens mount their controls after a fetch). A missing
 * anchor degrades to a centred card; a step on another screen gets
 * "Take me there" through the `navigate` bridge; a video step without a
 * video explains itself and Next continues.
 *
 * Phones (2026-09-17): the card is a bottom sheet, so the spotlight only has
 * the viewport ABOVE it. The overlay publishes the sheet's height as a CSS
 * custom property (`TOUR_SHEET_VAR`) the shell and the Studio pad by, and
 * scrolls each anchor into that free zone (`sheetScrollDelta`). Controls that
 * live in a closed drawer on phones — the sidebar rows, the Studio chat — are
 * held open by their owners for the step that points at them: `onStep`
 * reports the active step to the app shell, the Studio reads `useTour()`.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useIsMobile } from "../../lib/useIsMobile";
import type { StorageLike } from "../../lib/trackedJobs";
import { TOUR_STEPS, isStepAvailable, positionOf, prevIndex, type TourRoute, type TourStep } from "../../lib/tour/steps";
import { CARD_GAP, TOUR_SHEET_VAR, placeCard, sheetScrollDelta, spotlightBox, type Box } from "../../lib/tour/placement";
import { advance, back, finishTour, isLastStep, loadTourState, saveTourState, skipTour, startTour, type TourState } from "../../lib/tour/state";
import { isTypingTarget } from "../../lib/tour/typingTarget";
import { TourContext, type TourContextValue } from "./tourContext";
import { TourWelcome } from "./TourWelcome";

export { useTour } from "./tourContext";

/** What a video step says when the app has nothing to open yet. */
const NO_VIDEO_HINT = "Once you have a video, this is where you open it.";

/** Delay before the one-time welcome appears — after the page's own enter
 *  animation, so the offer reads as a greeting and not a pop-up. */
const WELCOME_DELAY_MS = 700;

const NULL_STORAGE: StorageLike = { getItem: () => null, setItem: () => {}, removeItem: () => {} };

function defaultStorage(): StorageLike {
  try {
    return window.localStorage;
  } catch {
    return NULL_STORAGE;
  }
}

export interface TourProviderProps {
  /** Take the app to a tour route — App maps it onto its own state. */
  navigate: (target: TourRoute) => void;
  /** The app has at least one rendered video to open. */
  hasVideo: boolean;
  /** The tour's reading of the current screen; null on screens the tour
   *  doesn't visit. */
  currentRoute: TourRoute | null;
  /** Offer the welcome card (only while the state is still "never"). */
  offerWelcome: boolean;
  /** The active step, whenever it changes (null when the tour is not
   *  running) — the app shell holds its phone drawer open on `nav-*` steps. */
  onStep?: (step: TourStep | null) => void;
  storage?: StorageLike;
  steps?: readonly TourStep[];
  children: ReactNode;
}

export function TourProvider({ navigate, hasVideo, currentRoute, offerWelcome, onStep, storage, steps = TOUR_STEPS, children }: TourProviderProps): React.JSX.Element {
  const store = useMemo(() => storage ?? defaultStorage(), [storage]);
  const [state, setState] = useState<TourState>(() => loadTourState(store));
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  useEffect(() => { saveTourState(store, state); }, [store, state]);

  const isActive = state.status === "active";
  const step = isActive ? steps[state.stepIdx] ?? null : null;
  useEffect(() => { onStep?.(step); }, [onStep, step]);

  // A persisted index past the end (the list shrank across a deploy) → done.
  useEffect(() => {
    if (isActive && !steps[state.stepIdx]) setState((s) => finishTour(s));
  }, [isActive, state.stepIdx, steps]);

  const start = useCallback(() => {
    setState(startTour());
    // Land on the first step's screen so the tour opens on a spotlight, not
    // on a "Take me there" card.
    if (steps[0]) navigateRef.current(steps[0].route);
  }, [steps]);
  const skip = useCallback(() => setState((s) => (s.status === "active" || s.status === "never" ? skipTour(s) : s)), []);
  const next = useCallback(() => setState((s) => advance(s, steps, hasVideo)), [steps, hasVideo]);
  const prev = useCallback(() => setState((s) => back(s, steps, hasVideo)), [steps, hasVideo]);
  // "Take me there" — jumps to the CURRENT step's route without touching
  // the step index (unlike the context's own `goToStep`, which jumps to an
  // arbitrary step — the Guide's per-section "Show me").
  const goCurrentRoute = useCallback(() => { if (step) navigateRef.current(step.route); }, [step]);
  const goToStep = useCallback((index: number) => {
    const target = steps[index];
    // Defence in depth: `advance`/`back` never land on a step whose anchor
    // needs a video the app doesn't have yet, and neither must a direct
    // jump — otherwise "Show me" strands the user on an inert card.
    if (!target || !isStepAvailable(target, hasVideo)) return;
    setState({ status: "active", stepIdx: index, version: 1 });
    navigateRef.current(target.route);
  }, [steps, hasVideo]);

  const ctx = useMemo<TourContextValue>(
    () => ({ start, goToStep, skip, isActive, step, hasVideo }),
    [start, goToStep, skip, isActive, step, hasVideo],
  );

  // The welcome: once, after a beat, only while nothing has been decided.
  const wantWelcome = offerWelcome && state.status === "never";
  const [welcomeReady, setWelcomeReady] = useState(false);
  useEffect(() => {
    if (!wantWelcome) { setWelcomeReady(false); return; }
    const t = window.setTimeout(() => setWelcomeReady(true), WELCOME_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [wantWelcome]);

  // Portaled to <body>: `#root { position: relative; z-index: 1 }` is its own
  // stacking context, so anything rendered inside it sits UNDER the modals
  // that already portal to body (SceneDetailModal, SmartExtendSection).
  const portalHost = typeof document !== "undefined" ? document.body : null;

  return (
    <TourContext.Provider value={ctx}>
      {children}
      {step && portalHost && createPortal(
        <TourOverlay
          step={step}
          index={state.stepIdx}
          steps={steps}
          hasVideo={hasVideo}
          currentRoute={currentRoute}
          isLast={isLastStep(state, steps, hasVideo)}
          onNext={next}
          onBack={prev}
          onSkip={skip}
          onGo={goCurrentRoute}
        />,
        portalHost,
      )}
      {wantWelcome && welcomeReady && portalHost && createPortal(<TourWelcome onStart={start} onDismiss={skip} />, portalHost)}
    </TourContext.Provider>
  );
}

// ---- overlay ---------------------------------------------------------------

interface Geom {
  rect: Box | null;
  vw: number;
  vh: number;
}

function sameBox(a: Box | null, b: Box | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

function anchorSelector(anchor: string): string {
  const esc = typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(anchor) : anchor.replace(/["\\]/g, "\\$&");
  return `[data-tour="${esc}"]`;
}

interface OverlayProps {
  step: TourStep;
  index: number;
  steps: readonly TourStep[];
  hasVideo: boolean;
  currentRoute: TourRoute | null;
  isLast: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  onGo: () => void;
}

function TourOverlay({ step, index, steps, hasVideo, currentRoute, isLast, onNext, onBack, onSkip, onGo }: OverlayProps): React.JSX.Element {
  const isMobile = useIsMobile();
  const cardRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<Element | null>(null);
  const scrolledForRef = useRef<string | null>(null);
  const [geom, setGeom] = useState<Geom>({ rect: null, vw: 0, vh: 0 });
  const [cardSize, setCardSize] = useState<{ width: number; height: number } | null>(null);

  const onRoute = currentRoute === step.route;
  const available = isStepAvailable(step, hasVideo);

  // The card's own size, for placement. Runs after every render; sets state
  // only on a real change so it can't loop. On phones it also publishes the
  // sheet's height (+ gap) as `TOUR_SHEET_VAR` on <html>: the shell pads the
  // page bottom by it and the Studio's chat drawer pads its composer, so the
  // last control on a page can be scrolled ABOVE the sheet instead of sitting
  // under it. Declared BEFORE the measure effect on purpose — layout effects
  // run in order, and the padding has to exist before the anchor is scrolled.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const width = el.offsetWidth;
    const height = el.offsetHeight;
    setCardSize((s) => (s && s.width === width && s.height === height ? s : { width, height }));
    if (isMobile) document.documentElement.style.setProperty(TOUR_SHEET_VAR, `${height + CARD_GAP}px`);
    else document.documentElement.style.removeProperty(TOUR_SHEET_VAR);
  });
  useLayoutEffect(() => () => { document.documentElement.style.removeProperty(TOUR_SHEET_VAR); }, []);

  // Measure the anchor — on step change and whenever the page moves. The
  // query does NOT depend on the route: a sidebar row is visible from every
  // screen, and a step whose control is already on screen gets its
  // spotlight, not a "Take me there" card.
  useLayoutEffect(() => {
    let raf = 0;
    const measure = (): void => {
      raf = 0;
      const el = available ? document.querySelector(anchorSelector(step.anchor)) : null;
      anchorRef.current = el;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (el) {
        if (scrolledForRef.current !== step.id) {
          scrolledForRef.current = step.id;
          // Centre, not "nearest": a control at the very edge of the viewport
          // leaves the card no room on that side.
          el.scrollIntoView({ block: "center", inline: "nearest" });
          // Phones: "centre" must mean the centre of the viewport ABOVE the
          // bottom sheet, or a low control lands under the card (QA
          // 2026-09-17, "Start the build"). One document nudge per step; the
          // sheet is laid out by now (layout effect), so its height is real.
          if (isMobile) {
            const sheet = cardRef.current?.offsetHeight ?? 0;
            const r0 = el.getBoundingClientRect();
            const delta = sheetScrollDelta(
              { top: r0.top, left: r0.left, width: r0.width, height: r0.height },
              sheet,
              { width: vw, height: vh },
            );
            if (delta !== 0) window.scrollBy(0, delta);
          }
        }
        const r = el.getBoundingClientRect();
        const rect: Box = { top: r.top, left: r.left, width: r.width, height: r.height };
        setGeom((g) => (sameBox(g.rect, rect) && g.vw === vw && g.vh === vh ? g : { rect, vw, vh }));
      } else {
        setGeom((g) => (g.rect === null && g.vw === vw && g.vh === vh ? g : { rect: null, vw, vh }));
      }
    };
    const schedule = (): void => {
      if (!raf) raf = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, true);
    // A control measured while its container is still sliding in (the phone
    // drawer's 220 ms translate, the page's 350 ms fade-up) lands the
    // spotlight where the control WAS mid-animation — a transform moves
    // nothing the observers below can see, so re-measure when it ends.
    document.addEventListener("animationend", schedule, true);
    document.addEventListener("transitionend", schedule, true);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    ro?.observe(document.body);
    const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(schedule) : null;
    mo?.observe(document.body, { childList: true, subtree: true });
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
      document.removeEventListener("animationend", schedule, true);
      document.removeEventListener("transitionend", schedule, true);
      ro?.disconnect();
      mo?.disconnect();
    };
  }, [step.id, step.anchor, onRoute, available, isMobile]);

  // The spotlight glides between steps but tracks the page rigidly while
  // scrolling — an eased box lagging behind a scroll reads as broken.
  const [animate, setAnimate] = useState(true);
  useEffect(() => {
    setAnimate(true);
    const t = window.setTimeout(() => setAnimate(false), 220);
    return () => window.clearTimeout(t);
  }, [step.id]);

  // Focus follows the step — and comes back after "Take me there", whose
  // button unmounts under the pointer once the route matches; leaves with
  // the tour, back to the anchor.
  useEffect(() => {
    cardRef.current?.focus({ preventScroll: true });
  }, [step.id, onRoute]);
  useEffect(() => () => {
    const el = anchorRef.current;
    if (el instanceof HTMLElement) el.focus({ preventScroll: true });
  }, []);

  // Esc = skip. The card is the topmost layer, so it owns Escape outright:
  // capture phase + stop, or the Studio's own Escape (back to the page)
  // would fire on the same press and the user would lose two things at once.
  // A text field keeps its own Escape (cancel a rename, close a Select).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape" || isTypingTarget(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      onSkip();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onSkip]);

  const spot = geom.rect ? spotlightBox(geom.rect) : null;
  const placed = spot && cardSize && !isMobile ? placeCard(spot, cardSize, { width: geom.vw, height: geom.vh }, step.placement) : null;
  const { n, total } = positionOf(index, steps, hasVideo);
  const hasPrev = prevIndex(index, steps, hasVideo) !== null;
  // "Take me there" only when the control is on another screen AND not on
  // this one — a sidebar row found from anywhere is simply spotlit.
  const offRoute = available && !onRoute && geom.rect === null;

  const cardPosition: CSSProperties = isMobile
    ? { left: 0, right: 0, bottom: 0, width: "100%", borderRadius: "16px 16px 0 0", padding: "16px 16px calc(16px + env(safe-area-inset-bottom))" }
    : placed
      ? { top: placed.top, left: placed.left }
      : { left: "50%", top: "50%", transform: "translate(-50%, -50%)" };

  return (
    <>
      <div aria-hidden style={{ position: "fixed", inset: 0, zIndex: 1000, pointerEvents: "none", overflow: "hidden" }}>
        {spot ? (
          <div
            style={{
              position: "absolute",
              top: spot.top,
              left: spot.left,
              width: spot.width,
              height: spot.height,
              borderRadius: 12,
              boxShadow: "0 0 0 9999px rgba(0,0,0,.55)",
              outline: "1.5px solid rgba(255,213,0,.55)",
              transition: animate ? "top .18s var(--ql-ease), left .18s var(--ql-ease), width .18s var(--ql-ease), height .18s var(--ql-ease)" : "none",
            }}
          />
        ) : (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.55)" }} />
        )}
      </div>
      <div
        ref={cardRef}
        role="dialog"
        aria-label={step.title}
        tabIndex={-1}
        className="ql-tour-card"
        style={{
          position: "fixed",
          zIndex: 1001,
          pointerEvents: "auto",
          width: isMobile ? "100%" : "min(320px, calc(100vw - 24px))",
          maxWidth: isMobile ? undefined : 320,
          padding: 16,
          borderRadius: 12,
          border: "1px solid var(--ql-border-strong)",
          background: "var(--ql-bg)",
          boxShadow: "0 18px 50px rgba(0,0,0,.55)",
          outline: "none",
          // First paint of a fresh step is a measuring pass; show it once placed.
          visibility: !isMobile && spot && !cardSize ? "hidden" : "visible",
          ...cardPosition,
        }}
      >
        <div style={{ fontFamily: "var(--ql-font-mono)", fontSize: 10.5, fontWeight: 600, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--ql-fg-subtle)", marginBottom: 6 }}>
          Step {n} of {total}
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ql-fg)", letterSpacing: "-0.01em", lineHeight: 1.3 }}>{step.title}</div>
        {/* Live region on the body only: the focused dialog already announces
            its label, so a live dialog would read every step twice. */}
        <p aria-live="polite" style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.55, color: "var(--ql-fg-muted)" }}>{step.body}</p>
        {!available && (
          <p style={{ margin: "8px 0 0", fontSize: 12.5, lineHeight: 1.5, color: "var(--ql-fg-subtle)", fontStyle: "italic" }}>{NO_VIDEO_HINT}</p>
        )}
        {/* Another screen: the way there is the main action, on its own
            full-width line; Next stays available as the quiet alternative. */}
        {offRoute && (
          <button type="button" onClick={onGo} style={{ ...PRIMARY, width: "100%", justifyContent: "center", marginTop: 14 }}>
            Take me there
          </button>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: offRoute ? 10 : 14 }}>
          <button type="button" onClick={onSkip} style={LINK}>Skip tour</button>
          <span style={{ flex: 1 }} />
          {hasPrev && <button type="button" onClick={onBack} style={GHOST}>Back</button>}
          <button type="button" onClick={onNext} style={offRoute ? GHOST : PRIMARY}>{isLast ? "Done" : "Next"}</button>
        </div>
      </div>
    </>
  );
}

const PRIMARY: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "8px 14px",
  borderRadius: 10,
  border: "none",
  fontWeight: 800,
  fontSize: 13,
  fontFamily: "inherit",
  color: "#000",
  background: "linear-gradient(135deg, var(--ql-yellow), #FFA500)",
  boxShadow: "0 4px 14px rgba(255,213,0,.25)",
  cursor: "pointer",
};

const GHOST: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,.12)",
  background: "transparent",
  color: "var(--ql-fg-muted)",
  fontWeight: 600,
  fontSize: 13,
  fontFamily: "inherit",
  cursor: "pointer",
};

const LINK: CSSProperties = {
  padding: "8px 2px",
  border: "none",
  background: "transparent",
  color: "var(--ql-fg-subtle)",
  fontSize: 12.5,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
  textDecoration: "underline",
  textUnderlineOffset: 3,
};
